/**
 * Layer panel - a fixed column beside the map, never over it.
 *
 * TIME-VARYING and WHOLE RUN are rasters, and exactly one is drawn at a time:
 * two stacked semi-transparent ramps make colours that are in neither legend.
 * REFERENCE layers are vectors and markers drawn above whichever raster is on.
 * Every row carries its own opacity and its own legend.
 *
 * Raster ids match `GET /api/runs/{id}/overlay/{layer}` exactly. Colours come
 * from Legend.tsx, which mirrors backend/render/colormaps.py.
 */

import { create } from 'zustand'

import { ABOVE_TOP, ARRIVAL_RAMP, HAZARD, HAZARD_LABELS, RAMPS, type RampStop } from './Legend'
import {
  LAYER_LABELS,
  STATIC_LAYERS,
  TIME_VARYING_LAYERS,
  type RasterLayer,
  useAppStore,
} from '../store/useAppStore'
import { C } from '../design/tokens'

export type StaticLayer = (typeof STATIC_LAYERS)[number]
export type ReferenceLayer = 'extent' | 'isochrones' | 'settlements' | 'river' | 'districts' | 'states'

export const RASTER_NAMES: Record<RasterLayer, string> = {
  ...LAYER_LABELS,
  max_dv: 'Depth × velocity',
  duration: 'Inundation duration',
  hazard: 'Hazard classification',
}

const RASTER_UNITS: Partial<Record<RasterLayer, string>> = {
  depth: 'm',
  velocity: 'm/s',
  max_depth: 'm',
  max_velocity: 'm/s',
  max_dv: 'm²/s',
  arrival: 'h',
  duration: 'h',
}

const REFERENCE_NAMES: Record<ReferenceLayer, string> = {
  extent: 'Flood extent outline',
  isochrones: 'Arrival isochrones',
  settlements: 'Settlement risk',
  river: 'Bhima river',
  districts: 'District boundaries',
  states: 'State boundaries',
}

/** Isochrone times, minutes after the breach. Those past the run's end are dropped. */
export const ISOCHRONE_MIN = [60, 120, 180, 360, 720, 1440]

export const RIVER_COLOUR = C.mapRiver
export const EXTENT_COLOUR = C.text
export const STATE_COLOUR = C.mapBoundary
export const DISTRICT_COLOUR = C.mapDistrict

const DEFAULT_OPACITY: Record<string, number> = {
  extent: 1,
  isochrones: 1,
  settlements: 1,
  river: 0.9,
  districts: 0.9,
  states: 1,
  compare: 0.8,
}

interface MapLayerState {
  resetNonce: number
  requestReset: () => void
  opacity: Record<string, number>
  visible: Record<ReferenceLayer, boolean>
  compare: boolean
  compareLayer: StaticLayer
  /** Divider position, 0 = left edge of the map, 1 = right edge. */
  comparePos: number
  setOpacity: (id: string, value: number) => void
  toggle: (id: ReferenceLayer) => void
  setCompare: (on: boolean) => void
  setCompareLayer: (layer: StaticLayer) => void
  setComparePos: (pos: number) => void
}

export const useMapLayers = create<MapLayerState>((set) => ({
  opacity: {},
  visible: { extent: false, isochrones: false, settlements: true, river: true, districts: true, states: true },
  compare: false,
  compareLayer: 'hazard',
  comparePos: 0.5,
  resetNonce: 0,
  requestReset: () => set((s) => ({ resetNonce: s.resetNonce + 1 })),
  setOpacity: (id, value) => set((s) => ({ opacity: { ...s.opacity, [id]: value } })),
  toggle: (id) => set((s) => ({ visible: { ...s.visible, [id]: !s.visible[id] } })),
  setCompare: (on) => set({ compare: on }),
  setCompareLayer: (layer) => set({ compareLayer: layer }),
  setComparePos: (pos) => set({ comparePos: Math.min(0.95, Math.max(0.05, pos)) }),
}))

export function opacityOf(state: Pick<MapLayerState, 'opacity'>, id: string): number {
  return state.opacity[id] ?? DEFAULT_OPACITY[id] ?? 0.8
}

function rgb(hex: string): [number, number, number] {
  const h = hex.replace('#', '')
  return [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16)) as [number, number, number]
}

/** The ramp's colour at a value, interpolated exactly as colormaps.sample_ramp does. */
export function rampColour(stops: RampStop[], value: number): string {
  const v = Math.min(Math.max(value, stops[0][0]), stops[stops.length - 1][0])
  for (let i = 1; i < stops.length; i += 1) {
    const [v0, c0] = stops[i - 1]
    const [v1, c1] = stops[i]
    if (v <= v1) {
      const t = (v - v0) / (v1 - v0 || 1)
      const a = rgb(c0)
      const b = rgb(c1)
      return `rgb(${a.map((x, k) => Math.round(x + (b[k] - x) * t)).join(', ')})`
    }
  }
  return stops[stops.length - 1][1].slice(0, 7)
}

export function formatHours(minutes: number): string {
  const h = minutes / 60
  return Number.isInteger(h) ? `${h} h` : `${h.toFixed(1)} h`
}

const isTransparent = (colour: string) => colour.length === 9 && colour.slice(7) === '00'

function tick(layer: RasterLayer, value: number): string {
  const v = layer === 'arrival' ? value / 60 : value
  return Number.isInteger(v) ? String(v) : String(Number(v.toFixed(1)))
}

/** Stepped swatches, one per ramp stop. The top stop reads "≥" because the
 *  renderer clamps: anything deeper than it takes that colour. */
function RampLegend({ layer }: { layer: RasterLayer }) {
  if (layer === 'hazard') {
    return (
      <div className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1">
        {[1, 2, 3, 4].map((cls) => (
          <span key={cls} className="flex items-center gap-2 text-12 text-[var(--ink-text-dim)]">
            <span className="h-3 w-4 rounded-[2px]" style={{ background: HAZARD[cls] }} />
            {HAZARD_LABELS[cls]}
          </span>
        ))}
      </div>
    )
  }
  const stops = RAMPS[layer]
  if (!stops) return null
  const above = ABOVE_TOP[layer]
  const shown: RampStop[] = stops.filter(([, colour]) => !isTransparent(colour))
  if (above) shown.push([Number.POSITIVE_INFINITY, above])
  const top = stops[stops.length - 1][0]
  const clampsAtTop = layer !== 'arrival' && !above
  return (
    <div className="mt-2">
      <div className="flex h-2.5 overflow-hidden rounded-[2px]">
        {shown.map(([value, colour]) => (
          <span key={value} className="flex-1" style={{ background: colour.slice(0, 7) }} />
        ))}
      </div>
      <div className="mt-1 flex font-mono text-12 tabular-nums text-[var(--ink-text-dim)]">
        {shown.map(([value], i) => (
          <span key={value} className="flex-1">
            {i === shown.length - 1 && clampsAtTop ? '≥' : ''}
            {Number.isFinite(value) ? tick(layer, value) : `>${tick(layer, top)}`}
          </span>
        ))}
      </div>
    </div>
  )
}

function LineSwatch({ colour, label }: { colour: string; label: string }) {
  return (
    <span className="flex items-center gap-2 text-12 text-[var(--ink-text-dim)]">
      <span className="h-0.5 w-5" style={{ background: colour }} />
      {label}
    </span>
  )
}

function ReferenceLegend({ layer, durationMin }: { layer: ReferenceLayer; durationMin: number | null }) {
  switch (layer) {
    case 'extent':
      return <LineSwatch colour={EXTENT_COLOUR} label="Peak wet area" />
    case 'river':
      return <LineSwatch colour={RIVER_COLOUR} label="Traced reach from the dam" />
    case 'districts':
      return <LineSwatch colour={DISTRICT_COLOUR} label="Census 2011, names from zoom 8" />
    case 'states':
      return <LineSwatch colour={STATE_COLOUR} label="Natural Earth 1:10m" />
    case 'isochrones': {
      const times = ISOCHRONE_MIN.filter((m) => durationMin === null || m <= durationMin)
      return (
        <div className="flex flex-wrap gap-x-3 gap-y-1">
          {times.map((m) => (
            <LineSwatch key={m} colour={rampColour(ARRIVAL_RAMP, m)} label={formatHours(m)} />
          ))}
        </div>
      )
    }
    case 'settlements':
      return (
        <div className="grid grid-cols-2 gap-x-3 gap-y-1">
          {[1, 2, 3, 4].map((cls) => (
            <span key={cls} className="flex items-center gap-2 text-12 text-[var(--ink-text-dim)]">
              <span className="h-2.5 w-2.5 rounded-full" style={{ background: HAZARD[cls] }} />
              {HAZARD_LABELS[cls]}
            </span>
          ))}
          <span className="flex items-center gap-2 text-12 text-[var(--ink-text-dim)]">
            <span className="h-2.5 w-2.5 rounded-full border border-[var(--ink-text-dim)]" />
            Dry
          </span>
        </div>
      )
  }
}

function Opacity({ id, name }: { id: string; name: string }) {
  const value = useMapLayers((s) => opacityOf(s, id))
  const setOpacity = useMapLayers((s) => s.setOpacity)
  return (
    <div className="mt-2 flex items-center gap-2">
      <input
        type="range"
        min={0}
        max={1}
        step={0.05}
        value={value}
        onChange={(event) => setOpacity(id, Number(event.target.value))}
        className="h-1 min-w-0 flex-1 cursor-pointer"
        style={{ accentColor: C.accent }}
        aria-label={`${name} opacity`}
      />
      <span className="w-10 text-right font-mono text-12 tabular-nums text-[var(--ink-text-dim)]">
        {Math.round(value * 100)}%
      </span>
    </div>
  )
}

function Row({
  type,
  checked,
  onChange,
  name,
  unit,
  id,
  children,
}: {
  type: 'radio' | 'checkbox'
  checked: boolean
  onChange: () => void
  name: string
  unit?: string
  id: string
  children: React.ReactNode
}) {
  return (
    <div
      className="border-t border-[var(--ink-border)] px-4 py-3 transition-shadow duration-150"
      style={{
        boxShadow: checked && type === 'radio' ? 'inset 2px 0 0 var(--ink-accent)' : undefined,
        transitionTimingFunction: 'cubic-bezier(0.2,0,0,1)',
      }}
    >
      <label className="flex cursor-pointer items-center gap-2.5">
        <input
          type={type}
          name={type === 'radio' ? 'raster-layer' : undefined}
          checked={checked}
          onChange={onChange}
          className="h-4 w-4 shrink-0 cursor-pointer"
          style={{ accentColor: C.accent }}
        />
        <span className={`flex-1 text-14 ${checked ? 'text-[var(--ink-text)]' : 'text-[var(--ink-text-dim)]'}`}>{name}</span>
        {unit && <span className="font-mono text-12 text-[var(--ink-text-dim)]">{unit}</span>}
      </label>
      <div className="pl-[26px]">
        <Opacity id={id} name={name} />
        {children}
      </div>
    </div>
  )
}

function Group({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="pb-2">
      <h3 className="px-4 pb-2 pt-4 text-12 font-medium uppercase tracking-[0.06em] text-[var(--ink-text-dim)]">
        {title}
      </h3>
      {children}
    </section>
  )
}

export function LayerPanel({ durationMin }: { durationMin: number | null }) {
  const activeLayer = useAppStore((s) => s.activeLayer)
  const setActiveLayer = useAppStore((s) => s.setActiveLayer)
  const visible = useMapLayers((s) => s.visible)
  const toggle = useMapLayers((s) => s.toggle)
  const compare = useMapLayers((s) => s.compare)
  const setCompare = useMapLayers((s) => s.setCompare)
  const compareLayer = useMapLayers((s) => s.compareLayer)
  const setCompareLayer = useMapLayers((s) => s.setCompareLayer)

  const raster = (layer: RasterLayer) => (
    <Row
      key={layer}
      type="radio"
      id={layer}
      name={RASTER_NAMES[layer]}
      unit={RASTER_UNITS[layer]}
      checked={activeLayer === layer}
      onChange={() => setActiveLayer(layer)}
    >
      <RampLegend layer={layer} />
    </Row>
  )

  return (
    <div className="pb-6">
      <div className="flex items-center justify-between gap-2 px-4 pt-4">
        <h2 className="text-20 font-semibold text-[var(--ink-text)]">Layers</h2>
        <button type="button" className="ctl" onClick={() => useMapLayers.getState().requestReset()} data-testid="reset-view">
          Reset view
        </button>
      </div>

      <Group title="Time-varying">{TIME_VARYING_LAYERS.map(raster)}</Group>
      <Group title="Whole run">{STATIC_LAYERS.map(raster)}</Group>

      <Group title="Reference">
        {(Object.keys(REFERENCE_NAMES) as ReferenceLayer[]).map((layer) => (
          <Row
            key={layer}
            type="checkbox"
            id={layer}
            name={REFERENCE_NAMES[layer]}
            checked={visible[layer]}
            onChange={() => toggle(layer)}
          >
            <div className="mt-2">
              <ReferenceLegend layer={layer} durationMin={durationMin} />
            </div>
          </Row>
        ))}
      </Group>

      <Group title="Compare">
        <Row
          type="checkbox"
          id="compare"
          name="Swipe compare"
          checked={compare}
          onChange={() => setCompare(!compare)}
        >
          <label className="mt-3 block text-12 uppercase tracking-[0.06em] text-[var(--ink-text-dim)]">
            Right of divider
            <select
              value={compareLayer}
              onChange={(event) => setCompareLayer(event.target.value as StaticLayer)}
              className="mt-1 block w-full rounded-[4px] border border-[var(--ink-border)] bg-[var(--ink-surface-raised)] px-2 py-1.5 text-14 normal-case tracking-normal text-[var(--ink-text)]"
            >
              {STATIC_LAYERS.map((layer) => (
                <option key={layer} value={layer}>
                  {RASTER_NAMES[layer]}
                </option>
              ))}
            </select>
          </label>
          <RampLegend layer={compareLayer} />
        </Row>
      </Group>
    </div>
  )
}
